import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import "./ContentHub.css";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  author_name: string;
  created_at: string;
  image_url: string;
  topic?: string;
  topic_display?: string;
}

const apiUrl =
  window._env_?.VITE_API_BASE_URL !== "${VITE_API_BASE_URL}"
    ? window._env_.VITE_API_BASE_URL
    : "/api";
const token = window._env_?.BASE_TOKEN;

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [post, setPost] = React.useState<BlogPost | null>(state?.post || null);
  const [loading, setLoading] = React.useState(!state?.post);
  const [error, setError] = React.useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post && slug) {
      const fetchPost = async () => {
        try {
          setLoading(true);
          const res = await axios.get(`${apiUrl}/external/api/blogs/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              slug,
              status: "published",
              source: "apollo",
            },
          });
          const blog = res.data.results?.find((b: BlogPost) => b.slug === slug);
          if (blog) setPost(blog);
          else setError("Blog post not found.");
        } catch (err) {
          setError("Blog post not found.");
        } finally {
          setLoading(false);
        }
      };
      fetchPost();
    }
  }, [slug, post]);

  if (loading) {
    return (
      <div className="blog-detail-wrapper">
        <Helmet>
          <title>Loading Blog... | Apollo Financial Blog</title>
        </Helmet>
        <div className="content-hub-hero loading-skeleton">
          <div className="content-hub-hero-content">
            <h3>Loading...</h3>
            <p>Please wait while we fetch the article.</p>
          </div>
        </div>
        <div className="blog-detail-content-container">
          <div className="detail-content">
            <p className="loading">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-detail-wrapper">
        <Helmet>
          <title>Error | Apollo Financial Blog</title>
        </Helmet>
        <div className="content-hub-hero error-banner">
          <div className="content-hub-hero-content">
            <h3>Oops!</h3>
            <p>{error}</p>
          </div>
        </div>
        <div className="blog-detail-content-container">
          <div className="detail-content">
            <p className="loading">{error}</p>
          </div>
          <button
            className="back-btn-content-hub"
            onClick={() => {
              navigate("/resources/content-hub/");
              window.scrollTo(0, 0);
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span style={{ marginLeft: "8px" }}>Go to Content Hub</span>
          </button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-detail-wrapper">
        <div className="content-hub-hero error-banner">
          <div className="content-hub-hero-content">
            <h3>Post data unavailable</h3>
            <p>Please go back.</p>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = React.useMemo(
    () =>
      new Date(post.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [post.created_at]
  );
  const topicDisplay = React.useMemo(
    () => post.topic_display,
    [post.topic_display]
  );

  return (
    <div className="blog-detail-wrapper">
      <Helmet>
        <title>{post.title} | Apollo Financial Blog</title>
        <meta name="description" content={post.content.slice(0, 150)} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.content.slice(0, 150)} />
        <meta property="og:image" content={post.image_url} />
        <meta property="og:type" content="article" />
      </Helmet>
      <div
        className="content-hub-hero"
        style={{
          backgroundImage: `url(${post.image_url})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundBlendMode: "overlay",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <div className="content-hub-hero-content">
          <h1>{post.title}</h1>
          <p>{formattedDate}</p>
          {topicDisplay && <p>{topicDisplay}</p>}
        </div>
      </div>
      <div className="blog-detail-content-container">
        <div
          className="detail-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        <button
          className="back-btn-content-hub"
          onClick={() => {
            if (window.history.state?.idx > 0) {
              navigate(-1);
            } else {
              navigate("/resources/content-hub/");
            }
            window.scrollTo(0, 0);
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span style={{ marginLeft: "8px" }}>
            {state?.post ? "Back" : "Go to Content Hub"}
          </span>
        </button>
      </div>
    </div>
  );
};

export default BlogDetail;
